import Cookies from "js-cookie";
import { Decrypt, EncryptBase64WithIV } from "../utils/common";
import { UserCookie } from "../utils/constants";

const getCliCookie = () => {
  try {
    const raw = Cookies.get("_cli");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getPortalClientId = () => {
  try {
    const userCookie = Cookies.get(UserCookie.CookieName);
    if (userCookie) {
      const parsed = JSON.parse(userCookie);
      const id = parseInt(parsed?.[UserCookie.ClientId], 10);
      if (id > 0) return id;
    }
    const cli = getCliCookie();
    if (!cli?.id) return 0;
    return parseInt(Decrypt(cli.id), 10) || 0;
  } catch {
    return 0;
  }
};

export const getPortalClientLogo = (loggedinUser) => {
  try {
    if (loggedinUser?.[UserCookie.ClientLogo]) return loggedinUser[UserCookie.ClientLogo];
    return getCliCookie()?.logo || "";
  } catch {
    return "";
  }
};

export const getPortalClientUrl = (loggedinUser) => {
  try {
    if (loggedinUser?.[UserCookie.ClientPortalUrl]) return loggedinUser[UserCookie.ClientPortalUrl];
    return getCliCookie()?.purl || "";
  } catch {
    return "";
  }
};

// ClientId held in the _cli cookie only (0 when missing or invalid).
export const getCliClientId = () => {
  try {
    const cli = getCliCookie();
    if (!cli?.id) return 0;
    return parseInt(Decrypt(cli.id), 10) || 0;
  } catch {
    return 0;
  }
};

// ClientId the logged-in user signed in under (0 = direct AssetsWatch login or not logged in).
export const getLoggedInClientId = () => {
  try {
    const userCookie = Cookies.get(UserCookie.CookieName);
    if (!userCookie) return 0;
    return parseInt(JSON.parse(userCookie)?.[UserCookie.ClientId], 10) || 0;
  } catch {
    return 0;
  }
};

// A user who signed in through a client must still have a matching _cli cookie.
// Direct AssetsWatch users (clid 0) never have one and are always valid.
export const isClientSessionValid = () => {
  const loggedInClientId = getLoggedInClientId();
  return loggedInClientId === 0 || getCliClientId() === loggedInClientId;
};

// Saves an incoming client ({ id, logo, purl, pfx }) as the _cli cookie. If a user is
// logged in under a different client (or directly), logs them out first so they
// sign in again under the incoming client.
export const setPortalClient = (client, isAuthenticated, logoutUser) => {
  const clientId = parseInt(client?.id, 10) || 0;
  if (clientId <= 0) return;
  if (isAuthenticated() && getLoggedInClientId() !== clientId) {
    logoutUser();
  }
  Cookies.set(
    "_cli",
    JSON.stringify({
      id: EncryptBase64WithIV(String(clientId)),
      logo: client.logo || "",
      purl: client.purl || "",
      pfx: client.pfx || "",
    }),
    { expires: 30 }
  );
};

// AssetsWatch's own public marketing pages. A leftover client logo is removed only here;
// every other page (sign-in flow, document/invoice links, checkout, ...) keeps _cli
// because client users can open those directly.
const OUTSIDE_PATHS = [
  "/home",
  "/aboutus",
  "/howitworks",
  "/plans",
  "/contactus",
  "/partners",
  "/properties",
  "/property",
  "/agents",
  "/agent",
  "/business",
  "/owner",
];

const isOutsidePath = (pathname) => {
  const path = (pathname || "").toLowerCase().replace(/\/+$/, "");
  if (path === "") return true; // home page "/"
  return OUTSIDE_PATHS.some((p) => path === p || path.startsWith(p + "/"));
};

// Drops a leftover _cli on outside pages so they show only the AssetsWatch logo.
// Kept when logged in under a client or when the URL is bringing a new _cli right now.
export const clearStaleClientCookie = (pathname = window.location.pathname) => {
  if (!isOutsidePath(pathname)) return;
  if (getLoggedInClientId() > 0) return;
  try {
    if (new URLSearchParams(window.location.search).has("_cli")) return;
  } catch {
    /* ignore */
  }
  Cookies.remove("_cli", { path: "/" });
};

// Re-saves the existing _cli with the same options as the login cookie (_u) so both
// expire together. No-op when there is no _cli (direct AssetsWatch users).
export const refreshPortalClientCookie = (options) => {
  const raw = Cookies.get("_cli");
  if (!raw) return;
  Cookies.set("_cli", raw, { ...options });
};
